<!-- CrudPedidos.vue — Orquestador principal -->
<!-- Maneja: carga de datos, filtros, sort, paginación, eliminar -->
<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar title="Pedidos" v-model="filtro" searchPlaceholder="# pedido o cliente...">
      <template #extra>
        <PedidosFiltros
          v-model:filtroFechaDesde="filtroFechaDesde"
          v-model:filtroFechaHasta="filtroFechaHasta"
        />
      </template>
      <template #actions>
        <button class="btn btn-exportar btn-header" @click="exportarExcel">
          <i class="bi bi-file-earmark-excel"></i>
          <span class="btn-header-texto ms-2">Exportar Excel</span>
        </button>
        <button class="btn btn-nuevo btn-header" @click="$refs.modalNuevo.abrir()">
          <i class="bi bi-gem"></i>
          <span class="btn-header-texto ms-2">Nuevo pedido</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- Tabla -->
    <PedidosTabla
      :pedidos="pedidosPaginados"
      :loading="loading"
      :paginaActual="paginaActual"
      :totalPaginas="totalPaginas"
      :totalRegistros="pedidosOrdenados.length"
      :rangoInicio="rangoInicio"
      :rangoFin="rangoFin"
      :paginasVisibles="paginasVisibles"
      :sortKey="sortKey"
      :sortDir="sortDir"
      :porPagina="porPagina"
      :opcionesPorPagina="paginacionStore.opcionesPorPagina"
      :itemsParaFiltroEstatus="pedidosPreEstatus"
      v-model:filtroEstatusSet="filtroEstatusSet"
      @ver-detalle="modalDetalle.abrir($event)"
      @editar-items="abrirEdicion($event)"
      @eliminar="confirmarEliminar"
      @cambiar-pagina="paginaActual = $event"
      @update:sort="onSort"
      @cambiar-por-pagina="cambiarPorPagina"
    />

    <!-- Modal nuevo pedido (wizard) -->
    <ModalNuevoPedido
      ref="modalNuevo"
      :catClientes="catClientes"
      :catArticulos="catArticulos"
      @guardado="cargarPedidos"
    />

    <!-- Modal detalle (solo lectura) -->
    <ModalDetallePedido ref="modalDetalle" />

    <ModalEditarItems
      ref="modalEditar"
      :catArticulos="catArticulos"
      @guardado="cargarPedidos"
    />

    <!-- Pedidos ya entregados: factura, guías, notas y proyectos -->
    <ModalEntregaPedido ref="modalEntrega" @guardado="cargarPedidos" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import Swal from "sweetalert2";
import {
  getClientes,
  getCatalogoPedido,
  getPedidos,
  eliminarPedido,
  cancelarPedido,
  exportarPedidos,
  reportarPedidos,
} from "@/utils/api_backend";
import { userPaginacionStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";

import PedidosFiltros from "./PedidosFiltros.vue";
import PedidosTabla from "./PedidosTabla.vue";
import ModalNuevoPedido from "./ModalNuevoPedido.vue";
import ModalDetallePedido from "./ModalDetallePedido.vue";
import ModalEditarItems from "./ModalEditarItems.vue";
import ModalEntregaPedido from "./ModalEntregaPedido.vue";

// ── Store compartido ─────────────────────────────────────
const paginacionStore = userPaginacionStore();

// ── Refs de modales ──────────────────────────────────────
const modalNuevo = ref(null);
const modalDetalle = ref(null);
const modalEditar = ref(null);
const modalEntrega = ref(null);

// Un pedido entregado ya no se edita: lo que queda es mandarle los documentos
// al cliente y dejar constancia. Por eso va a un modal distinto.
function abrirEdicion(pedido) {
  if (pedido.estatus_almacen === "Entregado") {
    modalEntrega.value.abrir(pedido);
  } else {
    modalEditar.value.abrir(pedido);
  }
}

// ── Catálogos y pedidos ──────────────────────────────────
const pedidos = ref([]);
const catClientes = ref([]);
const catArticulos = ref([]);
const loading = ref(false);
const enviandoReporte = ref(false);

// ── Filtros ──────────────────────────────────────────────
const filtro = ref("");
const filtroEstatusSet = ref(new Set());
const filtroFechaDesde = ref("");
const filtroFechaHasta = ref("");

// ── Ordenamiento ─────────────────────────────────────────
const sortKey = ref("fecha_recibo");
const sortDir = ref("desc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

// ── Paginación ────────────────────────────────────────────
const paginaActual = ref(1);
const porPagina = computed(() => paginacionStore.porPagina);

function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

// Resetear página al filtrar
watch(
  [filtro, filtroEstatusSet, filtroFechaDesde, filtroFechaHasta],
  () => {
    paginaActual.value = 1;
  }
);

// ── Computed ──────────────────────────────────────────────
// Texto + rango de fechas — universo que alimenta las opciones del
// embudo de Estatus almacén (para que reflejen la búsqueda/fechas
// activas, sin excluirse a sí mismo).
const pedidosPreEstatus = computed(() =>
  pedidos.value.filter((p) => {
    const q = filtro.value.toLowerCase();
    const matchTexto =
      String(p.numero_pedido).includes(q) ||
      p.cliente?.toLowerCase().includes(q) ||
      p.cliente_nombre?.toLowerCase().includes(q) ||
      p.cliente_numero?.toLowerCase().includes(q);
    const matchFechaDesde =
      !filtroFechaDesde.value || p.fecha_entrega_real >= filtroFechaDesde.value;
    const matchFechaHasta =
      !filtroFechaHasta.value || p.fecha_entrega_real <= filtroFechaHasta.value;
    return matchTexto && matchFechaDesde && matchFechaHasta;
  })
);

const pedidosFiltrados = computed(() =>
  pedidosPreEstatus.value.filter(
    (p) => !filtroEstatusSet.value.size || filtroEstatusSet.value.has(p.estatus_almacen)
  )
);

const pedidosOrdenados = computed(() => {
  return [...pedidosFiltrados.value].sort((a, b) => {
    let valA = a[sortKey.value] ?? "";
    let valB = b[sortKey.value] ?? "";
    const cmp = String(valA).localeCompare(String(valB), "es", { sensitivity: "base" });
    return sortDir.value === "asc" ? cmp : -cmp;
  });
});

const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(pedidosOrdenados.value.length / porPagina.value))
);

const pedidosPaginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return pedidosOrdenados.value.slice(inicio, inicio + porPagina.value);
});

const rangoInicio = computed(() =>
  pedidosOrdenados.value.length === 0 ? 0 : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, pedidosOrdenados.value.length)
);

const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

// ── Carga ─────────────────────────────────────────────────
async function cargarPedidos() {
  loading.value = true;
  try {
    pedidos.value = await getPedidos();
  } catch {
    Swal.fire("Error", "No se pudieron cargar los pedidos.", "error");
  } finally {
    loading.value = false;
  }
}

// ── Eliminar / Cancelar ───────────────────────────────────
async function confirmarEliminar(p) {
  const { isDismissed, value: accion } = await Swal.fire({
    title: `Pedido #${p.numero_pedido}`,
    html: `<p style="color:#6c757d;font-size:0.9rem;margin:0">Cliente: <strong>${p.cliente}</strong></p>`,
    icon: "warning",
    showCloseButton: true,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: '<i class="bi bi-x-circle me-1"></i>Cancelar pedido',
    denyButtonText: '<i class="bi bi-trash me-1"></i>Eliminar pedido',
    cancelButtonText: "Volver",
    confirmButtonColor: "#e65100",
    denyButtonColor: "#c0392b",
    cancelButtonColor: "#6c757d",
    reverseButtons: false,
  });

  if (isDismissed) return;

  const esCancelar = accion === true;

  const titulo = esCancelar
    ? `Motivo de cancelación — Pedido #${p.numero_pedido}`
    : `Motivo de eliminación — Pedido #${p.numero_pedido}`;
  const placeholder = esCancelar
    ? "¿Por qué se cancela este pedido?"
    : "¿Por qué se elimina este pedido?";

  const { value: motivo, isConfirmed } = await Swal.fire({
    title: titulo,
    input: "textarea",
    inputPlaceholder: placeholder,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: esCancelar ? "Confirmar cancelación" : "Confirmar eliminación",
    confirmButtonColor: esCancelar ? "#e65100" : "#c0392b",
    cancelButtonText: "Volver",
    cancelButtonColor: "#6c757d",
    inputValidator: (value) => {
      if (!value?.trim()) return "El motivo es obligatorio";
    },
  });

  if (!isConfirmed) return;

  try {
    if (esCancelar) {
      await cancelarPedido(p.numero_pedido, motivo.trim());
      Swal.fire({
        icon: "success",
        title: "Pedido cancelado",
        timer: 2000,
        showConfirmButton: false,
      });
    } else {
      await eliminarPedido(p.numero_pedido, motivo.trim());
      Swal.fire({
        icon: "success",
        title: "Pedido eliminado",
        timer: 2000,
        showConfirmButton: false,
      });
    }
    await cargarPedidos();
  } catch {
    Swal.fire("Error", "No se pudo completar la operación.", "error");
  }
}

// ── Reporte WhatsApp ──────────────────────────────────────
async function enviarReporteWhatsapp() {
  const { isConfirmed } = await Swal.fire({
    title: "Enviar reporte por WhatsApp",
    html: `<p style="color:#6c757d;font-size:0.9rem;margin:0">
      Se enviará el reporte de pedidos entregados hoy y en proceso
      a los destinatarios registrados.
    </p>`,
    icon: "question",
    confirmButtonText: '<i class="bi bi-whatsapp me-1"></i>Enviar',
    confirmButtonColor: "#25d366",
    cancelButtonText: "Cancelar",
    showCancelButton: true,
    cancelButtonColor: "#6c757d",
  });

  if (!isConfirmed) return;

  enviandoReporte.value = true;
  try {
    await reportarPedidos();
    Swal.fire({
      icon: "success",
      title: "Reporte enviado",
      text: "Los destinatarios recibirán el mensaje en breve.",
      timer: 2500,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo enviar el reporte.", "error");
  } finally {
    enviandoReporte.value = false;
  }
}

// ── Exportar Excel ───────────────────────────────────────
async function exportarExcel() {
  const hoy = new Date();
  const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const ultimoDia = hoy.toISOString().split("T")[0];

  const { value: fechas, isConfirmed } = await Swal.fire({
    title: "Exportar pedidos a Excel",
    html: `
      <div style="text-align:left;margin-bottom:8px">
        <label style="font-size:0.85rem;font-weight:700;color:var(--d-green);display:block;margin-bottom:4px">
          Fecha inicio (fecha recibo)
        </label>
        <input id="swal-fecha-inicio" type="date" value="${primerDia}"
          class="swal2-input" style="margin:0;width:100%">
      </div>
      <div style="text-align:left;margin-top:12px">
        <label style="font-size:0.85rem;font-weight:700;color:var(--d-green);display:block;margin-bottom:4px">
          Fecha fin (fecha recibo)
        </label>
        <input id="swal-fecha-fin" type="date" value="${ultimoDia}"
          class="swal2-input" style="margin:0;width:100%">
      </div>
    `,
    confirmButtonText: '<i class="bi bi-download me-1"></i>Descargar',
    confirmButtonColor: "var(--d-green-dark)",
    cancelButtonText: "Cancelar",
    showCancelButton: true,
    preConfirm: () => {
      const inicio = document.getElementById("swal-fecha-inicio").value;
      const fin = document.getElementById("swal-fecha-fin").value;
      if (!inicio || !fin) {
        Swal.showValidationMessage("Ambas fechas son obligatorias");
        return false;
      }
      if (inicio > fin) {
        Swal.showValidationMessage("La fecha inicio no puede ser mayor a la fecha fin");
        return false;
      }
      return { inicio, fin };
    },
  });

  if (!isConfirmed || !fechas) return;

  try {
    await exportarPedidos(fechas.inicio, fechas.fin);
  } catch {
    Swal.fire("Error", "No se pudo generar el archivo Excel.", "error");
  }
}

// ── Mount ─────────────────────────────────────────────────
onMounted(async () => {
  try {
    // catArticulos = productos + servicios activos, con la misma forma que el
    // antiguo catálogo de artículos ({ id, codigo, descripcion }) más `tipo`;
    // `id` es la clave "P:<código>" / "S:<código>".
    const [clientes, catalogo] = await Promise.all([getClientes(), getCatalogoPedido()]);
    catClientes.value = clientes;
    catArticulos.value = catalogo;
  } catch {
    Swal.fire("Error", "No se pudieron cargar catálogos.", "error");
  }
  await cargarPedidos();
});
</script>

<style scoped>
.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: #703808;
  color: var(--d-cream);
}
.btn-reporte {
  background-color: #25d366;
  color: #ffffff;
  border: none;
  font-weight: 600;
}
.btn-reporte:hover:not(:disabled) {
  background-color: #1da851;
  color: #ffffff;
}
.btn-reporte:disabled {
  opacity: 0.65;
}
.btn-exportar {
  background-color: var(--d-green);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-exportar:hover:not(:disabled) {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}
</style>
